import React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../helpers/cn";
import { isNavPathActive } from "../../helpers/adminNav";
import type { AdminNavGroupItem } from "../../config/adminNav";
import { AdminNavLink, NAV_ROW_BASE } from "./AdminNavLink";

interface AdminNavGroupProps {
  group: AdminNavGroupItem;
  pathname: string;
  isOpen: boolean;
  containsActive: boolean;
  onToggle: () => void;
}

export const AdminNavGroup: React.FC<AdminNavGroupProps> = ({
  group,
  pathname,
  isOpen,
  containsActive,
  onToggle,
}) => {
  const panelId = `adminnav-panel-${group.id}`;
  const Icon = group.icon;

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={cn(
          NAV_ROW_BASE,
          "w-full text-left text-black hover:bg-primary-100",
          // Collapsed but holding the active route: a lighter tint than the real
          // active pill, so it hints without impersonating the selected row.
          containsActive &&
            !isOpen &&
            "bg-primary-100 text-primary hover:bg-primary-200",
          containsActive && isOpen && "text-primary"
        )}
      >
        <Icon
          className="w-[18px] h-[18px] shrink-0"
          strokeWidth={1.75}
          aria-hidden="true"
          focusable="false"
        />
        <span className="truncate">{group.label}</span>
        <ChevronDown
          className={cn(
            "w-4 h-4 shrink-0 ml-auto text-gray-500 transition-transform duration-200",
            !isOpen && "-rotate-90"
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <ul id={panelId} className="mt-1 flex flex-col gap-0.5">
          {group.items.map((leaf) => (
            <li key={leaf.id}>
              <AdminNavLink
                to={leaf.path}
                label={leaf.label}
                isActive={isNavPathActive(pathname, leaf)}
                variant="sub"
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
